import { type CustomerAnalyticsMoodysReportDto } from '@zg-rentals/ts-customer-analytics-client';
import { Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import { MarketTrendsColumn, MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import { ConstructionAbsorptionTable } from './ConstructionAbsorptionTable';
import { getRowStyle } from './pdfImageUtils';
import { formatCurrency } from '../formatCurrency';

const nameWidthPercent = 25;
const numberWidthPercent = 18.75;

export const ConcessionsConstructionAbsorptionPage = ({
  report,
  showConcessions,
  showConstructionAbsorption,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  showConcessions: boolean;
  showConstructionAbsorption: boolean;
}) => {
  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        {showConcessions && (
          <>
            <MarketTrendsTextHeader headerText="Concessions: Effective Rent, Expenses, Free Rent" paddingBottom={20} />
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={nameWidthPercent} textAlign="left">
                  <Text>Submarket Name</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={numberWidthPercent}>
                  <Text>Free Rent</Text>
                  <Text style={styles.tableSubHeading}>Mos</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={numberWidthPercent}>
                  <Text>Expenses</Text>
                  <Text style={styles.tableSubHeading}>%</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={numberWidthPercent}>
                  <Text>Effective Rent</Text>
                  <Text style={styles.tableSubHeading}>Units</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={numberWidthPercent}>
                  <Text>Effective Rent % Change</Text>
                  <Text style={styles.tableSubHeading}>Units</Text>
                </MarketTrendsColumn>
              </View>
              {report.concessionsReport
                .sort((a, b) => a.submarketName.localeCompare(b.submarketName))
                .map((submarketData, i) => (
                  <View style={getRowStyle(i)} key={submarketData.submarketName}>
                    <MarketTrendsColumn width={nameWidthPercent} textAlign="left">
                      <Text> {submarketData.submarketName}</Text>
                    </MarketTrendsColumn>
                    <MarketTrendsColumn width={numberWidthPercent}>
                      <Text> {submarketData.freeRent}</Text>
                    </MarketTrendsColumn>
                    <MarketTrendsColumn width={numberWidthPercent}>
                      <Text> {submarketData.expenses}%</Text>
                    </MarketTrendsColumn>
                    <MarketTrendsColumn width={numberWidthPercent}>
                      <Text> {formatCurrency(submarketData.effectiveRent)}</Text>
                    </MarketTrendsColumn>
                    <MarketTrendsColumn width={numberWidthPercent}>
                      <Text> {(submarketData.effectiveRentChange * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  </View>
                ))}
            </View>
          </>
        )}
        {showConstructionAbsorption && (
          <>
            <ConstructionAbsorptionTable
              header="Annualized Construction/Absorption (Historical 5 Year and Forecasted)"
              leftReportName="5 Year History"
              rightReportName="5 Year Forecast"
              leftReports={report.constructionAbsorptionReport.fiveYearHistory}
              rightReports={report.constructionAbsorptionReport.fiveYearForecast}
            />
            <ConstructionAbsorptionTable
              header="Quarterly Construction/Absorption (Current and Previous Quarters)"
              leftReportName={report.constructionAbsorptionReport.currentQuarter[0].timePeriod}
              rightReportName={report.constructionAbsorptionReport.lastQuarter[0].timePeriod}
              leftReports={report.constructionAbsorptionReport.currentQuarter}
              rightReports={report.constructionAbsorptionReport.lastQuarter}
            />
          </>
        )}
      </View>
    </MarketTrendsPageWrapper>
  );
};
